import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Alert, Button } from 'camino-react'

import { TAM, MANAGED, InvoicingReportContext } from '../InvoicingReportContext'

import ReportContent from './ReportContent'

import { cloudhelpPatch, cloudhelpGet } from 'Utilities/CloudHelp'
import TicketInvoiceReportCard from 'Components/Reports/Invoicing/ReportCard'
import TicketInvoicingSection from 'Components/Tickets/Invoicing'
import Modal from 'Components/Modal'

class TicketInvoicingReport extends React.Component {
  constructor (props) {
    super(props)
    const { activeFilters: { startTime, endTime, clientsList } } = props
    const [managedClients, timeAndMaterialsClients] = _.partition(clientsList, c => c.isManaged)

    this.state = {
      reviewedTimesheets: [],
      notReviewedTimesheets: [],
      ticketsToApprove: [],
      filters: {
        canShowReviewed: true,
        loading: false,
        startTime,
        endTime,
        clientType: MANAGED,
        clients: { [TAM]: timeAndMaterialsClients, [MANAGED]: managedClients },
        showReviewed: false,
        clientsSelected: [],
        allClients: false
      }
    }
  }

  gatherData = () => {
    const { paths: { apiTicketInvoicingReportPath } } = this.props
    const {
      filters:
        {
          startTime,
          endTime,
          clientsSelected
        }
    } = this.state
    const selectedClientIds = _.map(clientsSelected, client => client.value)
    const params = { report: { start_time: startTime, end_time: endTime, client_ids: selectedClientIds } }
    this.updateFilters({ loading: true })

    cloudhelpGet(apiTicketInvoicingReportPath, params)
      .then(({ data: { reviewedTimesheets, notReviewedTimesheets } }) => {
        this.setState({ reviewedTimesheets, notReviewedTimesheets })
        this.updateFilters({ loading: false })
      })
  }

  ticketToApproveToParameters = ticket => ({
    id: ticket.id,
    chargeable_rate: ticket.chargeable_rate,
    timesheets: _.map(ticket.timesheets, timesheet => (
      {
        id: timesheet.id,
        description: timesheet.description,
        start_time: timesheet.start_time,
        end_time: timesheet.end_time,
        work_type_id: timesheet.work_type_id,
        chargeable_rate: timesheet.chargeable_rate,
        chargeable: timesheet.chargeable
      }
    ))
  })

  // Submit the tickets that are checked for approval i.e. in the ticketsToApprove state variable
  submitMultipleTickets = () => {
    const { ticketsToApprove } = this.state
    const { paths: { multipleInvoicingPath } } = this.props
    const patchData = { tickets: _.map(ticketsToApprove, ticket => this.ticketToApproveToParameters(ticket)) }

    cloudhelpPatch(multipleInvoicingPath, patchData)
      .then(({ data: { data } }) => {
        const { tickets, message } = data
        const timesheets = _.flatMap(tickets, ticket => (
          _.map(ticket.timesheets, t => ({ ...t, ticket }))
        ))

        this.setTimesheetsToReviewed(timesheets)
        this.setState({ alert: { type: 'success', message } })
      })
      .catch(({ response: { data: { errors: message } } }) => {
        this.setState({ alert: { type: 'error', message: _.flatten(message) } })
      })
  }

  // Allow us to update the report filters from child components
  updateFilters = (newFilterState, stateCallback) => {
    this.setState(
      prevState => ({ filters: { ...prevState.filters, ...newFilterState } }),
      stateCallback
    )
  }

  updateFiltersClientType = (newFilterState, stateCallback) => {
    this.setState(
      prevState => ({ filters: {
        ...prevState.filters,
        ...newFilterState,
        clientsSelected: [],
        allClients: false
      } }),
      stateCallback
    )
  }

  updateSelectAllClients = (value) => {
    this.setState(
      prevState => ({ filters:
        {
          ...prevState.filters,
          allClients: value,
          clientsSelected: value ? prevState.filters.clients[prevState.filters.clientType] : []
        } })
    )
  }

  // Allow us to update the general report state from child components
  updateReportState = (newState, stateCallback) => {
    this.setState(newState, stateCallback)
  }

  // After reviewing the timesheets for a ticket we remove the ticket from the notReviewed list and
  // mark it as reviewed, in order to do this we need to remove it from the reviewed list in case of re-reviewing
  // and then adding the new updated timesheets from the ticket
  setTimesheetsToReviewed = (updatedTimesheets) => {
    this.setState((prevState) => {
      const { reviewedTimesheets, notReviewedTimesheets } = prevState
      const timesheets = _.map(updatedTimesheets, t => ({ ...t, ticket: { ...t.ticket, invoiced: true } }))

      _.remove(notReviewedTimesheets, timesheet => _(timesheets).map('id').includes(timesheet.id))
      _.remove(reviewedTimesheets, timesheet => _(timesheets).map('id').includes(timesheet.id))

      return { reviewedTimesheets: [...reviewedTimesheets, ...timesheets], notReviewedTimesheets }
    })
  }

  invoicingSubmitCallback = (updatedTimesheets) => {
    this.setTimesheetsToReviewed(updatedTimesheets)
    this.updateReportState({ currentTicket: null })
  }

  timesheetsOrInvoiced = (timesheets, invoiced) => _.filter(
    timesheets,
    timesheet => (invoiced ? !_.includes(_.map(timesheets, 'original_timesheet_id'), timesheet.id) : true)
  )

  render () {
    const {
      reviewedTimesheets,
      notReviewedTimesheets,
      filters,
      currentTicket,
      ticketsToApprove,
      alert
    } = this.state

    const { localStoragePrefix, paths: { workRolesPath, workTypesPath } } = this.props
    const {
      updateFilters,
      updateReportState,
      gatherData,
      invoicingSubmitCallback,
      submitMultipleTickets,
      updateSelectAllClients,
      updateFiltersClientType
    } = this
    const { showReviewed, clientsSelected, loading, allClients } = filters

    const selectedClientIds = _.map(clientsSelected, client => client.value)
    const timesheetsToShow = showReviewed ? [...reviewedTimesheets, ...notReviewedTimesheets] : notReviewedTimesheets
    const timesheets = _.flatten(
      _.filter(_.filter(timesheetsToShow, t => _.includes(selectedClientIds, t.clientId)), (timesheet) => {
        if (timesheet.ticket.invoiced) {
          return !_.includes(_.map(timesheetsToShow, 'original_timesheet_id'), timesheet.id)
        }

        return true
      })
    )

    const reportState = {
      filters,
      updateFilters,
      updateReportState,
      gatherData,
      allClients,
      updateSelectAllClients,
      updateFiltersClientType
    }

    return (
      <InvoicingReportContext.Provider value={reportState}>
        {!_.isNil(currentTicket) && (
          <Modal removeModal={() => updateReportState({ currentTicket: null })} cardSizeClass="">
            <TicketInvoicingSection
              card={false}
              ticket={currentTicket}
              submitCallback={invoicingSubmitCallback}
              timesheets={this.timesheetsOrInvoiced(currentTicket.timesheets, currentTicket.invoiced)}
              paths={[
                { name: 'workRoles', path: workRolesPath },
                { name: 'workTypes', path: workTypesPath },
                { name: 'invoiceTimesheets', path: currentTicket.invoiceTimesheetsPath },
                { name: 'clientServiceGroups', path: currentTicket.clientServiceGroupsPath }
              ]}
            />
          </Modal>
        )}

        <TicketInvoiceReportCard>
          {alert && (
            <Alert type={alert.type}>
              <ul>
                {_.flatMap(Array(alert.message), msg => <li>{msg}</li>)}
              </ul>
            </Alert>
          )}

          <Button disabled={_.isEmpty(ticketsToApprove)} onClick={() => submitMultipleTickets()}>
            Approve chosen tickets
          </Button>

          <ReportContent
            {...{
              updateReportState,
              ticketsToApprove,
              showReviewed,
              loading,
              timesheets,
              localStoragePrefix,
              gatherData,
              allClients,
              updateSelectAllClients
            }}
          />
        </TicketInvoiceReportCard>
      </InvoicingReportContext.Provider>
    )
  }
}

TicketInvoicingReport.propTypes = {
  paths: PropTypes.shape({
    apiTicketInvoicingReportPath: PropTypes.string.isRequired,
    workRolesPath: PropTypes.string.isRequired,
    workTypesPath: PropTypes.string.isRequired
  }).isRequired,
  activeFilters: PropTypes.shape({
    startTime: PropTypes.string,
    endTime: PropTypes.string
  }),
  localStoragePrefix: PropTypes.string,
  clientsSelected: PropTypes.arrayOf(PropTypes.number)
}

TicketInvoicingReport.defaultProps = {
  activeFilters: {
    start_time: new Date(),
    end_time: new Date()
  },
  localStoragePrefix: 'ticketInvoicingReport',
  clientsSelected: []
}

export default TicketInvoicingReport
